import { search } from './searchService';
import { getSilent, getById, put, get, post, del } from './apiService';
import type { Business, BusinessProfile } from '../types/business';
import type { RetellCallScore } from '../types/callReport';
import type { SearchParams } from '../types/search';

export const TEAM_SEARCH_EXPAND = 'client,teamSummary';

export interface BusinessFilter {
  clientId?: string;
  demo?: boolean;
}

export async function searchBusinesses(
  filter: BusinessFilter = {},
  params: SearchParams = {},
): Promise<Business[]> {
  return search<Business>('/teams/search', filter, params);
}

export async function searchAllBusinesses(
  filter: BusinessFilter = {},
  params: Omit<SearchParams, 'page'> = {},
): Promise<Business[]> {
  const pageSize = params.pageSize ?? 1000;
  const businesses: Business[] = [];
  let page = 1;

  while (true) {
    const batch = await searchBusinesses(filter, { ...params, page, pageSize });
    businesses.push(...batch);
    if (batch.length < pageSize) break;
    page += 1;
  }

  return businesses;
}

export async function getBusinessById(id: string, queryParams?: string): Promise<Business> {
  return getById<Business>('/business', id, queryParams);
}

export async function associateClient(businessId: string, clientId: string): Promise<Business> {
  return put<Business>(`/business/${businessId}/client/${clientId}`, {});
}

export async function toggleDemo(businessId: string): Promise<Business> {
  return put<Business>(`/business/${businessId}/demo`, {});
}

export async function addSeats(businessId: string, totalSeats: number): Promise<void> {
  return post('/business/add-seats', { businessId, totalSeats });
}

export async function createBusinessInvite(data: {
  email: string;
  displayName: string;
  teamName: string;
  startingSeats: number;
  demo: boolean;
  clientId?: string;
  propertyId?: string;
}): Promise<unknown> {
  return post('/create-business/invite', data);
}

export async function updateShopStartDate(
  teamId: string,
  shopStartDate: string,
): Promise<void> {
  return put(`/business/${teamId}/active-plan/shop-start-date`, { shopStartDate });
}

export async function updatePlanStatus(teamId: string, status: string): Promise<void> {
  return put(`/business/${teamId}/active-plan/status`, { status });
}

export async function updateCollisionWindow(teamId: string, phoneAliasCollisionWindow: number): Promise<void> {
  return put(`/business/${teamId}/active-plan/collision-window`, { phoneAliasCollisionWindow });
}

export async function updateAgentMode(teamId: string, agentMode: string): Promise<void> {
  return put(`/business/${teamId}/active-plan/agent-mode`, { agentMode });
}

export async function updateOnboardingProgress(teamId: string, progress: number): Promise<void> {
  return put(`/business/${teamId}/active-plan/onboarding-progress`, { progress });
}

export async function completeOnboarding(teamId: string, data: { clientPlatform?: string; agentMode?: string }): Promise<void> {
  return put(`/business/${teamId}/active-plan/onboarding-completed`, data);
}

export async function saveScoreCategories(
  teamId: string,
  scoreCategories: RetellCallScore[],
): Promise<void> {
  return put(`/business/${teamId}/active-plan/scoring`, { scoreCategories });
}

export async function addProfileToBusiness(data: {
  email: string;
  displayName: string;
  businessId: string;
  persona: string;
  role: string;
  delayNotification: boolean;
}): Promise<unknown> {
  return post('/add-profile-to-business', data);
}

export async function addProfileRole(businessId: string, profileId: string, role: string): Promise<unknown> {
  return put(`/team/${businessId}/profile/${profileId}?role=${role}`, {});
}

export async function removeProfileRole(businessId: string, profileId: string, role: string): Promise<unknown> {
  return del(`/team/${businessId}/profile/${profileId}?role=${role}`);
}

export async function resendInvitation(businessId: string, profileId: string): Promise<unknown> {
  return put(`/business/${businessId}/profile/${profileId}/resend-notification`, {});
}

export async function getNotificationLink(businessId: string, profileId: string): Promise<{ link: string } | null> {
  return getSilent<{ link: string }>(`/business/${businessId}/profile/${profileId}/get-notification-link`);
}

export async function suspendProfile(businessId: string, profileId: string): Promise<unknown> {
  return put(`/business/${businessId}/profile/${profileId}/suspend`, {});
}

export async function activateProfile(businessId: string, profileId: string): Promise<unknown> {
  return put(`/business/${businessId}/profile/${profileId}/activate`, {});
}

export async function removeProfile(businessId: string, profileId: string): Promise<unknown> {
  return put(`/business/${businessId}/profile/${profileId}/remove`, {});
}

export async function getRemovedProfiles(businessId: string): Promise<BusinessProfile[]> {
  return get<BusinessProfile[]>(`/business/${businessId}/removed-profiles`);
}
